// Two sign-in modes behind one interface:
//   auth0 — @auth0/auth0-vue, SPA + PKCE, shared audience (auth0-architecture.md)
//   dev   — local impersonation for building before the Auth0 `convention`
//           application exists. The API refuses dev mode in production, so a
//           dev token can never authenticate against a deployed server.
import { ref, watch, type App } from 'vue'
import { createAuth0 } from '@auth0/auth0-vue'

export type AuthMode = 'auth0' | 'dev'

export const authMode: AuthMode =
  import.meta.env.VITE_AUTH_MODE ?? (import.meta.env.VITE_AUTH0_DOMAIN ? 'auth0' : 'dev')

const DEV_KEY = 'convention-dev-token'
let auth0: ReturnType<typeof createAuth0> | null = null

function readDevToken(): string | null {
  try { return localStorage.getItem(DEV_KEY) } catch { return null }
}

/** Reactive sign-in state for the shell and the router guard. */
export const signedIn = ref<boolean>(authMode === 'dev' ? readDevToken() != null : false)
export const authReady = ref<boolean>(authMode === 'dev')

export function installAuth(app: App): void {
  if (authMode !== 'auth0') return
  auth0 = createAuth0({
    domain: import.meta.env.VITE_AUTH0_DOMAIN!,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID!,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/callback`,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? 'https://api.natca.org',
    },
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
  })
  app.use(auth0)
  const client = auth0
  // Mirror the SDK's reactive state into ours.
  watch([client.isAuthenticated, client.isLoading], ([authed, loading]) => {
    signedIn.value = authed
    authReady.value = !loading
  }, { immediate: true })
}

export async function authorizationHeader(): Promise<string | null> {
  if (authMode === 'dev') {
    const t = readDevToken()
    return t ? `Dev ${t}` : null
  }
  if (!auth0 || !auth0.isAuthenticated.value) return null
  return `Bearer ${await auth0.getAccessTokenSilently()}`
}

export async function signIn(devToken?: string, returnTo = '/'): Promise<void> {
  if (authMode === 'dev') {
    if (!devToken) throw new Error('dev sign-in needs a member number')
    try { localStorage.setItem(DEV_KEY, devToken) } catch { /* private mode: session only */ }
    signedIn.value = true
    return
  }
  // The SDK's appState target is not routed (auth0 installs before the
  // router), so the callback page reads the destination from here.
  try { sessionStorage.setItem(RETURN_KEY, returnTo) } catch { /* private mode: lands home */ }
  await auth0!.loginWithRedirect({ appState: { target: returnTo } })
}

const RETURN_KEY = 'convention-return-to'

/** Where to go after the Auth0 callback; read once. Same-origin paths only. */
export function consumeReturnTo(): string | null {
  try {
    const v = sessionStorage.getItem(RETURN_KEY)
    sessionStorage.removeItem(RETURN_KEY)
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null
  } catch {
    return null
  }
}

export async function signOut(): Promise<void> {
  if (authMode === 'dev') {
    try { localStorage.removeItem(DEV_KEY) } catch { /* ignore */ }
    signedIn.value = false
    return
  }
  await auth0!.logout({ logoutParams: { returnTo: window.location.origin } })
}

/** Resolves once Auth0 has finished loading (including a redirect callback). */
export async function waitForAuth(): Promise<void> {
  if (authReady.value) return
  await new Promise<void>((resolve) => {
    const stop = watch(authReady, (ready) => {
      if (ready) { stop(); resolve() }
    })
  })
}
